import { GetterTree, ActionTree, MutationTree, ActionContext } from 'vuex';
import { cloneDeep } from 'lodash';
import { MultiSelectOptionInterface } from '@/components/forms/select/app-select';

export interface SettingsValues {
  currency: string;
  locationName: string;
  dateFormat: string;
  timezone: string;
  defaultSender: string;
  defaultEmail: string;
  mandrillApiKey: string;
  postcodeLookupApiKey: string;
  regionalMailchimpApiKey: string;
}

export interface SettingsValuesExtended extends SettingsValues {
  availableCurrencies: MultiSelectOptionInterface[];
  availableTimezones: MultiSelectOptionInterface[];
}

export const defaultSettingsState: SettingsValuesExtended = {
  currency: '',
  locationName: '',
  dateFormat: '',
  timezone: '',
  defaultSender: '',
  defaultEmail: '',
  mandrillApiKey: '',
  postcodeLookupApiKey: '',
  regionalMailchimpApiKey: '',
  availableCurrencies: [],
  availableTimezones: []
};

const state: SettingsValuesExtended = cloneDeep(defaultSettingsState);

const mutations: MutationTree<SettingsValuesExtended> = {
  ADD_CURRENCY(returnedState: SettingsValuesExtended, payload: string): void {
    returnedState.currency = payload;
  },
  ADD_LOCATION_NAME(returnedState: SettingsValuesExtended, payload: string): void {
    returnedState.locationName = payload;
  },
  ADD_DATE_FORMAT(returnedState: SettingsValuesExtended, payload: string): void {
    returnedState.dateFormat = payload;
  },
  ADD_TIMEZONE(returnedState: SettingsValuesExtended, payload: string): void {
    returnedState.timezone = payload;
  },
  ADD_DEFAULT_SENDER(returnedState: SettingsValuesExtended, payload: string): void {
    returnedState.defaultSender = payload;
  },
  ADD_DEFAULT_EMAIL(returnedState: SettingsValuesExtended, payload: string): void {
    returnedState.defaultEmail = payload;
  },
  ADD_MANDRILL_API_KEY(returnedState: SettingsValuesExtended, payload: string): void {
    returnedState.mandrillApiKey = payload;
  },
  ADD_POSTCODE_LOOKUP_API_KEY(returnedState: SettingsValuesExtended, payload: string): void {
    returnedState.postcodeLookupApiKey = payload;
  },
  ADD_REGIONAL_MAILCHIMP_API_KEY(returnedState: SettingsValuesExtended, payload: string): void {
    returnedState.regionalMailchimpApiKey = payload;
  },
  ADD_AVAILABLE_CURRENCIES(returnedState: SettingsValuesExtended, payload: MultiSelectOptionInterface[]): void {
    returnedState.availableCurrencies = payload;
  },
  ADD_AVAILABLE_TIMEZONES(returnedState: SettingsValuesExtended, payload: MultiSelectOptionInterface[]): void {
    returnedState.availableTimezones = payload;
  }
};

const actions: ActionTree<SettingsValuesExtended, {}> = {
  addCurrency: ({ commit }: ActionContext<SettingsValuesExtended, {}>, payload: string): void => {
    commit('ADD_CURRENCY', payload);
  },
  addLocationName: ({ commit }: ActionContext<SettingsValuesExtended, {}>, payload: string): void => {
    commit('ADD_LOCATION_NAME', payload);
  },
  addDateFormat: ({ commit }: ActionContext<SettingsValuesExtended, {}>, payload: string): void => {
    commit('ADD_DATE_FORMAT', payload);
  },
  addTimezone: ({ commit }: ActionContext<SettingsValuesExtended, {}>, payload: string): void => {
    commit('ADD_TIMEZONE', payload);
  },
  addDefaultSender: ({ commit }: ActionContext<SettingsValuesExtended, {}>, payload: string): void => {
    commit('ADD_DEFAULT_SENDER', payload);
  },
  addDefaultEmail: ({ commit }: ActionContext<SettingsValuesExtended, {}>, payload: string): void => {
    commit('ADD_DEFAULT_EMAIL', payload);
  },
  addMandrillApiKey: ({ commit }: ActionContext<SettingsValuesExtended, {}>, payload: string): void => {
    commit('ADD_MANDRILL_API_KEY', payload);
  },
  addPostcodeLookupKey: ({ commit }: ActionContext<SettingsValuesExtended, {}>, payload: string): void => {
    commit('ADD_POSTCODE_LOOKUP_API_KEY', payload);
  },
  addRegionalMailchimpApiKey: ({ commit }: ActionContext<SettingsValuesExtended, {}>, payload: string): void => {
    commit('ADD_REGIONAL_MAILCHIMP_API_KEY', payload);
  },
  addAvailableCurrencies: (
    { commit }: ActionContext<SettingsValuesExtended, {}>,
    payload: MultiSelectOptionInterface[]
  ): void => {
    commit('ADD_AVAILABLE_CURRENCIES', payload);
  },
  addAvailableTimezones: (
    { commit }: ActionContext<SettingsValuesExtended, {}>,
    payload: MultiSelectOptionInterface[]
  ): void => {
    commit('ADD_AVAILABLE_TIMEZONES', payload);
  }
};

const getters: GetterTree<SettingsValuesExtended, {}> = {
  currency: (returnedState: SettingsValuesExtended): string => {
    return returnedState.currency;
  },
  locationName: (returnedState: SettingsValuesExtended): string => {
    return returnedState.locationName;
  },
  dateFormat: (returnedState: SettingsValuesExtended): string => {
    return returnedState.dateFormat;
  },
  timezone: (returnedState: SettingsValuesExtended): string => {
    return returnedState.timezone;
  },
  defaultSender: (returnedState: SettingsValuesExtended): string => {
    return returnedState.defaultSender;
  },
  defaultEmail: (returnedState: SettingsValuesExtended): string => {
    return returnedState.defaultEmail;
  },
  mandrillApiKey: (returnedState: SettingsValuesExtended): string => {
    return returnedState.mandrillApiKey;
  },
  postcodeLookupApiKey: (returnedState: SettingsValuesExtended): string => {
    return returnedState.postcodeLookupApiKey;
  },
  regionalMailchimpApiKey: (returnedState: SettingsValuesExtended): string => {
    return returnedState.regionalMailchimpApiKey;
  },
  availableCurrencies: (returnedState: SettingsValuesExtended): MultiSelectOptionInterface[] => {
    return returnedState.availableCurrencies;
  },
  availableTimezones: (returnedState: SettingsValuesExtended): MultiSelectOptionInterface[] => {
    return returnedState.availableTimezones;
  },
  currencySymbol: (returnedState: SettingsValuesExtended): string | null => {
    return (
      returnedState.availableCurrencies
        .find((option: MultiSelectOptionInterface): boolean => option.value === returnedState.currency)
        ?.label.toString() || null
    );
  },
  isSettingsMissing: (returnedState: SettingsValuesExtended): boolean => {
    return Object.values(returnedState).some((item: string | null): boolean => !item);
  }
};

export default {
  state,
  mutations,
  actions,
  getters
};
